import React, { FC, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { useApolloClient } from '@apollo/client';
import { useToaster } from '@/components/core/Toaster';
import {
  SEND_SCHEDULED_ANALYTICS_REPORT,
  SendScheduledAnalyticsReportData,
  SendScheduledAnalyticsReportVars,
} from '@/graph/group-performance';

type ReportType = 'weekly' | 'monthly';

/**
 * Super-admin debug control (shift-D) that sends the scheduled weekly or monthly
 * analytics PDF to the saved recipients, through the same task the cron job runs.
 */
const SendScheduledAnalyticsReport: FC = () => {
  const client = useApolloClient();
  const toaster = useToaster();
  const [sending, setSending] = useState<ReportType | null>(null);

  const send = async (reportType: ReportType) => {
    const label = reportType === 'weekly' ? 'weekly' : 'monthly';
    const confirmed = window.confirm(
      `Send the ${label} analytics PDF to the configured ${label} report recipients?\n\n` +
        'This uses the same send path as the scheduled report. Unsaved configuration changes are not included.',
    );
    if (!confirmed) return;

    setSending(reportType);
    try {
      const result = await client.mutate<SendScheduledAnalyticsReportData, SendScheduledAnalyticsReportVars>({
        mutation: SEND_SCHEDULED_ANALYTICS_REPORT,
        variables: { reportType },
      });
      const data = result.data?.sendScheduledAnalyticsReport;
      if (data?.success) {
        toaster.success(data.message || `${label} analytics report sent`);
      } else {
        toaster.error(data?.message || `${label} analytics report was not sent`);
      }
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'unknown error';
      toaster.error(`${label} analytics report failed: ${message}`);
    } finally {
      setSending(null);
    }
  };

  return (
    <Box
      mb={2}
      p={1.5}
      style={{
        border: '1px dashed rgba(0, 0, 0, 0.2)',
        borderRadius: 4,
        flexShrink: 0,
      }}
    >
      <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
        Debug: send the saved weekly or monthly analytics PDF to the configured recipients, using the scheduled report
        path.
      </Typography>
      <Box display="flex" style={{ gap: 8 }}>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={sending !== null}
          onClick={() => send('weekly')}
          startIcon={sending === 'weekly' ? <CircularProgress size={14} /> : <Send style={{ fontSize: 14 }} />}
        >
          {sending === 'weekly' ? 'Sending...' : 'Send weekly report'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          disabled={sending !== null}
          onClick={() => send('monthly')}
          startIcon={sending === 'monthly' ? <CircularProgress size={14} /> : <Send style={{ fontSize: 14 }} />}
        >
          {sending === 'monthly' ? 'Sending...' : 'Send monthly report'}
        </Button>
      </Box>
    </Box>
  );
};

export default SendScheduledAnalyticsReport;
